import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import "@/App.css";

/**
 * INDUSMEN — Archival single-viewport homage.
 * No scroll. One artefact: a gold wordmark against a cinematic
 * montage of Indian commercial architecture.
 */

const FRAMES = [
  {
    src: "https://images.pexels.com/photos/10958529/pexels-photo-10958529.jpeg?auto=compress&cs=tinysrgb&w=2400",
    place: "DLF Corporate Greens",
    city: "Gurugram · Haryana",
  },
  {
    src: "https://images.pexels.com/photos/5859963/pexels-photo-5859963.jpeg?auto=compress&cs=tinysrgb&w=2400",
    place: "Bandra Kurla Complex",
    city: "Mumbai · Maharashtra",
  },
  {
    src: "https://images.pexels.com/photos/15863408/pexels-photo-15863408.jpeg?auto=compress&cs=tinysrgb&w=2400",
    place: "Nine Square, Sector 62",
    city: "Noida · Uttar Pradesh",
  },
  {
    src: "https://images.pexels.com/photos/15670701/pexels-photo-15670701.jpeg?auto=compress&cs=tinysrgb&w=2400",
    place: "Manyata Tech Park",
    city: "Bengaluru · Karnataka",
  },
  {
    src: "https://images.pexels.com/photos/5859965/pexels-photo-5859965.jpeg?auto=compress&cs=tinysrgb&w=2400",
    place: "Cyber Hub · DLF Phase II",
    city: "Gurugram · Haryana",
  },
  {
    src: "https://images.pexels.com/photos/30400430/pexels-photo-30400430.jpeg?auto=compress&cs=tinysrgb&w=2400",
    place: "Marine Drive Skyline",
    city: "Mumbai · Maharashtra",
  },
];

const MANIFESTO = [
  { n: "I.", w: "COMMERCIAL" },
  { n: "II.", w: "CAPITAL" },
  { n: "III.", w: "CONVICTION" },
  { n: "IV.", w: "CRAFT" },
  { n: "V.", w: "CONTINUITY" },
];

// Masked stripe reveal for the logo image
const LogoReveal = () => {
  return (
    <div
      data-testid="hero-logo"
      className="relative flex items-center justify-center"
      aria-label="indusmen"
    >
      {/* soft gold halo */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 -m-24 rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(196,183,147,0.28), rgba(196,183,147,0.06) 60%, transparent 75%)",
          filter: "blur(30px)",
        }}
      />
      <div className="relative">
        {/* 8 stripes reveal from bottom */}
        <div className="relative">
          <img
            src="/brand/indusmen-logo-hi.png"
            alt="indusmen"
            draggable={false}
            className="block w-[min(74vw,880px)] h-auto select-none opacity-0"
          />
          <div className="absolute inset-0 flex">
            {Array.from({ length: 10 }).map((_, i) => (
              <motion.div
                key={i}
                initial={{ y: "101%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{
                  duration: 1.15,
                  delay: 0.55 + i * 0.055,
                  ease: [0.76, 0, 0.24, 1],
                }}
                className="relative flex-1 overflow-hidden"
                style={{ willChange: "transform" }}
              >
                <div
                  className="absolute inset-0 bg-no-repeat bg-cover"
                  style={{
                    backgroundImage: "url(/brand/indusmen-logo-hi.png)",
                    backgroundSize: `${10 * 100}% 100%`,
                    backgroundPosition: `${(i / 9) * 100}% center`,
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* faint reflection */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.18 }}
          transition={{ duration: 2, delay: 1.8 }}
          className="pointer-events-none mt-2 mx-auto h-16 w-[min(74vw,880px)]"
          style={{
            maskImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.55), transparent)",
            WebkitMaskImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.55), transparent)",
            transform: "scaleY(-1)",
            backgroundImage: "url(/brand/indusmen-logo-hi.png)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% 100%",
            filter: "blur(1.5px)",
          }}
        />
      </div>
    </div>
  );
};

const Word = ({ text, delay = 0, className = "" }) => {
  const letters = useMemo(() => Array.from(text), [text]);
  return (
    <span className={className} aria-label={text}>
      {letters.map((c, i) => (
        <motion.span
          key={i}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          transition={{
            duration: 0.9,
            delay: delay + i * 0.025,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="inline-block"
          aria-hidden
        >
          {c === " " ? "\u00A0" : c}
        </motion.span>
      ))}
    </span>
  );
};

export default function App() {
  const [i, setI] = useState(0);
  const rootRef = useRef(null);

  // slideshow
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % FRAMES.length), 5200);
    return () => clearInterval(t);
  }, []);

  // subtle parallax on mouse move
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 40, damping: 20, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 40, damping: 20, mass: 0.6 });
  const bgX = useTransform(smx, [-1, 1], [18, -18]);
  const bgY = useTransform(smy, [-1, 1], [12, -12]);
  const fgX = useTransform(smx, [-1, 1], [-10, 10]);
  const fgY = useTransform(smy, [-1, 1], [-6, 6]);

  useEffect(() => {
    const onMove = (e) => {
      const { innerWidth: w, innerHeight: h } = window;
      mx.set((e.clientX / w) * 2 - 1);
      my.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [mx, my]);

  const cur = FRAMES[i];

  return (
    <div
      ref={rootRef}
      data-testid="indusmen-hero"
      className="fixed inset-0 h-[100dvh] w-screen overflow-hidden bg-[#0a0906] text-[#efe7dc]"
      style={{
        fontFamily:
          "'Cormorant Garamond', 'Lora', ui-serif, Georgia, serif",
      }}
    >
      {/* Background slideshow with Ken Burns */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={i}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="absolute inset-0"
              style={{
                x: bgX,
                y: bgY,
              }}
            >
              <motion.img
                src={cur.src}
                alt=""
                aria-hidden
                draggable={false}
                className="absolute inset-0 h-[112%] w-[112%] -top-[6%] -left-[6%] object-cover"
                initial={{ scale: 1.05 }}
                animate={{ scale: 1.18 }}
                transition={{ duration: 8, ease: "linear" }}
                style={{ filter: "grayscale(0.35) contrast(1.05) brightness(0.62) saturate(0.85)" }}
              />
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Vignette + gold wash */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 50%, rgba(10,9,6,0.15) 0%, rgba(10,9,6,0.55) 55%, rgba(10,9,6,0.92) 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 mix-blend-overlay opacity-40"
          style={{
            background:
              "linear-gradient(180deg, rgba(196,183,147,0.10), transparent 30%, transparent 70%, rgba(196,183,147,0.18))",
          }}
        />
        {/* grain */}
        <div aria-hidden className="absolute inset-0 pointer-events-none grain" />
        {/* film scanline top/bottom */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-[1px]"
          style={{ background: "linear-gradient(90deg, transparent, rgba(196,183,147,0.55), transparent)" }}
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-[1px]"
          style={{ background: "linear-gradient(90deg, transparent, rgba(196,183,147,0.35), transparent)" }}
        />
      </div>

      {/* Foreground content */}
      <motion.div className="relative h-full w-full" style={{ x: fgX, y: fgY }}>
        {/* Top bar */}
        <div className="absolute top-0 inset-x-0 px-6 md:px-10 pt-6 md:pt-8 flex items-start justify-between text-[10.5px] md:text-[11px] tracking-[0.32em] uppercase font-mono text-[#c4b793]">
          <div className="flex items-center gap-3">
            <motion.span
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block h-[6px] w-[6px] rotate-45 bg-[#c4b793]"
            />
            <Word text="EST. INDIA" delay={0.35} />
            <span className="opacity-40 hidden md:inline">/</span>
            <Word text="N° 01 — ONE OF ONE" delay={0.55} className="hidden md:inline" />
          </div>
          <div className="text-right">
            <Word text="COMMERCIAL REAL ESTATE" delay={0.45} />
            <div className="mt-1 opacity-60">
              <Word text="ARCHIVE · MMXXV" delay={0.7} />
            </div>
          </div>
        </div>

        {/* Center — logo */}
        <div className="absolute inset-0 flex items-center justify-center px-8">
          <div className="flex flex-col items-center">
            <motion.div
              initial={{ letterSpacing: "1.2em", opacity: 0 }}
              animate={{ letterSpacing: "0.42em", opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mb-6 md:mb-8 text-[10px] md:text-[11px] uppercase font-mono text-[#c4b793]/80"
            >
              a study in indian real estate
            </motion.div>

            <LogoReveal />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 md:mt-10 max-w-xl text-center italic text-[15px] md:text-[17px] leading-[1.55] text-[#efe7dc]/85"
            >
              <span className="text-[#c4b793]">“</span>
              Investing for everyone — a quiet monument to towers,
              transactions and the tenants who filled them.
              <span className="text-[#c4b793]">”</span>
            </motion.div>

            {/* thin rule */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.6, delay: 1.9, ease: [0.76, 0, 0.24, 1] }}
              className="mt-8 h-[1px] w-40 bg-[#c4b793]/60 origin-left"
            />
          </div>
        </div>

        {/* Bottom bar */}
        <div className="absolute bottom-0 inset-x-0 px-6 md:px-10 pb-6 md:pb-8 flex items-end justify-between text-[10.5px] md:text-[11px] tracking-[0.32em] uppercase font-mono text-[#c4b793]">
          <div className="max-w-[40vw]">
            <div className="opacity-60 mb-1">
              <Word text="NOW SHOWING" delay={1.2} />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={cur.place}
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -8, opacity: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="frame-caption"
              >
                <div className="text-[#efe7dc] text-[11.5px] md:text-[13px] tracking-[0.24em]">
                  {cur.place}
                </div>
                <div className="opacity-70">{cur.city}</div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* frame counter */}
          <div className="text-right">
            <div className="opacity-60 mb-1">
              <Word text="FRAME" delay={1.2} />
            </div>
            <div className="text-[#efe7dc] text-[13px] md:text-[15px] font-mono tracking-[0.2em] tabular-nums">
              {String(i + 1).padStart(2, "0")}
              <span className="opacity-40"> / {String(FRAMES.length).padStart(2, "0")}</span>
            </div>
          </div>
        </div>

        {/* Left edge — vertical marquee (manifesto) */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-[42px] md:w-[54px] flex items-center justify-center overflow-hidden">
          <div
            className="marquee-vertical text-[11px] md:text-[12px] tracking-[0.5em] uppercase font-mono text-[#c4b793]/70 whitespace-nowrap"
            style={{ writingMode: "vertical-rl" }}
          >
            {Array.from({ length: 3 }).map((_, r) => (
              <span key={r} className="inline-block">
                {MANIFESTO.map((m, idx) => (
                  <span key={idx} className="mx-8">
                    <span className="text-[#c4b793]">{m.n}</span>
                    <span className="mx-3 opacity-40">·</span>
                    {m.w}
                    <span className="mx-8 opacity-40">◇</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* Right edge — coordinates ticker */}
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-[42px] md:w-[54px] flex items-center justify-center">
          <div
            className="text-[10px] md:text-[11px] tracking-[0.42em] uppercase font-mono text-[#c4b793]/70"
            style={{ writingMode: "vertical-rl" }}
          >
            <Word text="28.6139° N — 77.2090° E" delay={1} />
          </div>
        </div>

        {/* Corner ticks */}
        {["tl", "tr", "bl", "br"].map((c) => (
          <motion.div
            key={c}
            aria-hidden
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.9, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute h-4 w-4 border-[#c4b793]/70 ${
              c === "tl"
                ? "top-4 left-4 border-t border-l"
                : c === "tr"
                  ? "top-4 right-4 border-t border-r"
                  : c === "bl"
                    ? "bottom-4 left-4 border-b border-l"
                    : "bottom-4 right-4 border-b border-r"
            }`}
          />
        ))}
      </motion.div>

      {/* Slow editorial marquee across the middle (per system reminder) */}
      <div className="pointer-events-none absolute left-0 right-0 top-[calc(50%+240px)] hidden md:block">
        <div className="marquee-horizontal whitespace-nowrap opacity-25">
          {Array.from({ length: 4 }).map((_, r) => (
            <span
              key={r}
              className="mx-8 text-[64px] leading-none italic text-[#c4b793]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              investing for everyone <span className="mx-6 not-italic">◆</span>
              commercial · capital · conviction · craft
              <span className="mx-6 not-italic">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* Load flash */}
      <motion.div
        aria-hidden
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 bg-[#0a0906] pointer-events-none"
      />
    </div>
  );
}
